/**
 * Yerel servis (local-service, varsayılan http://localhost:3003) çağrıları için
 * yedek adrese düşme kuralı — tüm yazarkasa entegrasyonlarında ortak.
 *
 * ESKİ DAVRANIŞ (tehlikeli): Ekran ayarındaki yerel servis adresine yapılan istek
 * hata fırlatınca AYNI istek localhost:3003'e tekrar gönderiliyordu. fetch'in hata
 * fırlatması isteğin cihaza ulaşmadığı anlamına gelmez (zaman aşımı, bağlantının
 * yanıt sırasında kopması); adres aynı servisi gösteriyorsa (localhost ↔ 127.0.0.1)
 * ödeme/satış komutu iki kez çalışıp ÇİFT ÇEKİM yapabiliyordu.
 *
 * YENİ KURAL:
 * - Yalnızca güvenli (okuma) çağrıları yedek adrese tekrar gönderilir: GET/HEAD ve
 *   durum/sorgu uç noktaları (aşağıdaki liste).
 * - Ödeme/satış gibi tekrarlanması güvenli olmayan çağrılar yeniden GÖNDERİLMEZ;
 *   hata çağırana iletilir (kasiyer sonucu cihazdan kontrol eder).
 * - Bir okuma çağrısı birincil adreste düşüp yedekte başarılı olduysa birincil
 *   adres bir süre "erişilemez" sayılır ve sonraki TÜM çağrılar doğrudan yedeğe
 *   gider (tek gönderim). Böylece yanlış yapılandırılmış adresle çalışan kurulumlar
 *   (bağlantı kontrolü önce yapılır) ödeme almaya devam eder.
 */

/** Tekrar gönderilmesi güvenli (yan etkisiz) uç noktalar — POST olsalar da yalnız okur. */
const SAFE_READ_ENDPOINTS = [
  'status',
  'device-info',
  'token-check-last-receipt',
  'pavopay-get-settings',
  'pavopay-get-sale-result',
  // Bağlantı kontrolleri (para hareketi yok): Pavo REST ve Inpos'un checkConnection'ı bunları
  // kullanır. Listede olmazlarsa yanlış adresle kurulmuş kasada yedek adres hiç "tercih edilir"
  // olmaz ve sonraki ödeme çağrısı (yedeğe tekrar gönderilmediği için) başarısız olur.
  'pavopay-pairing',
  'pavopay-get-device-info',
  'pavopay-query-customer',
  'inpos-get-state',
  'gmp3-echo',
  'gmp3-check-open-transaction',
  'gmp3-get-departments',
  'gmp3-get-tax-rates',
];

/** Birincil adres erişilemez bulunduktan sonra doğrudan yedeğe gidilecek süre */
const PREFER_FALLBACK_MS = 10 * 60 * 1000;

/** birincil taban adres → bu zamana kadar doğrudan yedeğe git */
const preferFallbackUntil = new Map<string, number>();

const endpointName = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    return path.split('/').filter(Boolean).pop() || '';
  } catch {
    return (url.split('?')[0].split('/').pop() || '');
  }
};

/** İstek yan etkisiz mi (yedek adrese tekrar gönderilebilir mi)? */
export const isSafeToResend = (url: string, options: RequestInit): boolean => {
  const method = (options.method || 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return true;
  return SAFE_READ_ENDPOINTS.includes(endpointName(url));
};

export const fetchLocalServiceWithFallback = async (
  url: string,
  options: RequestInit,
  primaryBaseUrl: string,
  defaultBaseUrl: string
): Promise<Response> => {
  if (primaryBaseUrl === defaultBaseUrl || !url.startsWith(primaryBaseUrl)) {
    return fetch(url, options);
  }
  const fallbackUrl = url.replace(primaryBaseUrl, defaultBaseUrl);
  const safe = isSafeToResend(url, options);

  // Birincil adres yakın zamanda erişilemez bulundu: doğrudan yedeğe (tek gönderim)
  if (Date.now() < (preferFallbackUntil.get(primaryBaseUrl) || 0)) {
    try {
      return await fetch(fallbackUrl, options);
    } catch (fallbackError) {
      preferFallbackUntil.delete(primaryBaseUrl);
      if (!safe) throw fallbackError;
      return fetch(url, options);
    }
  }

  try {
    return await fetch(url, options);
  } catch (primaryError) {
    if (!safe) {
      console.warn(
        `[LocalService] Birincil URL başarısız (${primaryBaseUrl}); "${endpointName(url)}" ödeme/satış çağrısı ` +
        `olduğu için yedek adrese (${defaultBaseUrl}) TEKRAR GÖNDERİLMEDİ (çift çekim önlemi).`
      );
      throw primaryError;
    }
    console.warn(`[LocalService] Birincil URL başarısız (${primaryBaseUrl}), fallback deneniyor: ${defaultBaseUrl}`);
    const response = await fetch(fallbackUrl, options);
    preferFallbackUntil.set(primaryBaseUrl, Date.now() + PREFER_FALLBACK_MS);
    return response;
  }
};
